import {
	Accordion,
	Code,
	Divider,
	Group,
	Stack,
	TextInput,
} from '@mantine/core';
import { Prism, type PrismProps } from '@mantine/prism';
import { IconSearch } from '@tabler/icons-react';
import { useState } from 'react';

import type { DebugLogItem } from '@repo/api-codegen';
import { useGetDebugLog, useGetDebugLogs } from '@repo/api-codegen';
import { Button, Text, Title } from '@repo/foundations';

interface DebugLogsProps {
	jobId: string;
}

export function DebugLogs({ jobId }: DebugLogsProps) {
	const [selectedLogIndex, setSelectedLogIndex] = useState<number | null>(null);
	const [visibleLogsCount, setVisibleLogsCount] = useState<number>(20);
	const [expandedContents, setExpandedContents] = useState<Set<number>>(
		new Set()
	);
	const [searchQuery, setSearchQuery] = useState<string>('');

	const { data: debugLogs, isLoading } = useGetDebugLogs({
		pathParams: {
			databuilderJobId: jobId,
		},
	});

	const { data: selectedLog, isLoading: isLoadingSelectedLog } = useGetDebugLog(
		{
			pathParams: {
				databuilderJobId: jobId,
				index: selectedLogIndex !== null ? selectedLogIndex : 0,
			},
		},
		{
			enabled: selectedLogIndex !== null,
		}
	);

	if (isLoading) {
		return <Text>Loading...</Text>;
	}

	if (!debugLogs?.length) {
		return null;
	}

	const handleLoadMore = () => {
		setVisibleLogsCount((prevCount) => prevCount + 20);
	};

	const handleLogSelect = (index: number) => {
		setSelectedLogIndex(index);
	};

	const toggleContentExpansion = (index: number) => {
		setExpandedContents((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	const isContentExpanded = (index: number) => expandedContents.has(index);

	const isJsonContent = (content?: string | null) => {
		if (!content) return false;

		try {
			JSON.parse(content);
			return true;
		} catch (e) {
			return false;
		}
	};

	const formatContent = (
		content?: string | null,
		contentType?: string | null
	) => {
		if (!content) return '';

		// If content can be parsed as JSON, format it
		if (isJsonContent(content)) {
			try {
				const jsonObj = JSON.parse(content);
				return JSON.stringify(jsonObj, null, 2);
			} catch (e) {
				return content;
			}
		}

		// Otherwise return as-is
		return content;
	};

	const truncateContent = (
		content?: string | null,
		contentType?: string | null
	) => {
		if (!content) return '';

		let formattedContent = '';

		// For JSON content, we'll use a smarter approach to ensure truncation preserves structure
		if (isJsonContent(content)) {
			try {
				const jsonObj = JSON.parse(content);
				const fullContent = JSON.stringify(jsonObj, null, 2);
				const lines = fullContent.split('\n');

				if (lines.length <= 20) return fullContent;

				// Take first 15 lines
				const firstPart = lines.slice(0, 15).join('\n');
				// Take last 2 lines (usually closing brackets)
				const lastPart = lines.slice(-2).join('\n');

				return firstPart + '\n...\n' + lastPart;
			} catch (e) {
				formattedContent = content;
			}
		} else {
			formattedContent = content;
		}

		// For non-JSON content, use simple line-based truncation
		const lines = formattedContent.split('\n');
		if (lines.length <= 20) return formattedContent;

		return lines.slice(0, 20).join('\n') + '\n...';
	};

	// Determine language for prism
	const getLanguage = (
		contentType?: string | null,
		content?: string | null
	): PrismProps['language'] => {
		// If content is JSON, use JSON language
		if (content && isJsonContent(content)) {
			return 'json';
		}

		// Default to typescript for syntax highlighting
		return 'typescript';
	};

	const filterLogsByTitle = (logs: DebugLogItem[]): DebugLogItem[] => {
		if (!searchQuery.trim()) return logs;

		return logs.filter((log) =>
			log.title.toLowerCase().includes(searchQuery.toLowerCase())
		);
	};

	const filteredLogs = filterLogsByTitle(debugLogs);
	const visibleLogs = filteredLogs.slice(0, visibleLogsCount);
	const hasMoreLogs = filteredLogs.length > visibleLogsCount;

	const shouldShowExpandButton = (content?: string | null) => {
		if (!content) return false;

		if (isJsonContent(content)) {
			try {
				const jsonObj = JSON.parse(content);
				const formatted = JSON.stringify(jsonObj, null, 2);
				return formatted.split('\n').length > 20;
			} catch (e) {
				return content.split('\n').length > 20;
			}
		}

		return content.split('\n').length > 20;
	};

	return (
		<Stack spacing="xs">
			<Divider />
			<Group position="apart">
				<Title size="md">Debug Logs</Title>
				<TextInput
					placeholder="Search by title"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					icon={<IconSearch size={16} />}
					style={{ width: '250px' }}
				/>
			</Group>

			{filteredLogs.length === 0 ? (
				<Text size="sm">No logs match your search criteria</Text>
			) : (
				<>
					<Accordion>
						{visibleLogs.map((logItem: DebugLogItem, index: number) => (
							<Accordion.Item
								key={`log-${logItem.title}-${logItem.created_at}`}
								value={`log-${index}`}
							>
								<Accordion.Control onClick={() => handleLogSelect(index)}>
									<Code>{logItem.title}</Code>
								</Accordion.Control>
								<Accordion.Panel>
									{selectedLogIndex === index &&
										(isLoadingSelectedLog ? (
											<Text>Loading...</Text>
										) : (
											<>
												<Prism
													language={getLanguage(
														selectedLog?.content_type,
														selectedLog?.content
													)}
													colorScheme="dark"
													copyLabel="Copy to clipboard"
													copiedLabel="Copied to clipboard"
												>
													{isContentExpanded(index)
														? formatContent(
																selectedLog?.content,
																selectedLog?.content_type
															)
														: truncateContent(
																selectedLog?.content,
																selectedLog?.content_type
															)}
												</Prism>
												{shouldShowExpandButton(selectedLog?.content) && (
													<Group position="right" mt="xs">
														<Button
															onClick={() => toggleContentExpansion(index)}
														>
															{isContentExpanded(index)
																? 'Show Less'
																: 'Show More'}
														</Button>
													</Group>
												)}
											</>
										))}
								</Accordion.Panel>
							</Accordion.Item>
						))}
					</Accordion>

					{hasMoreLogs && (
						<Group position="center" mt="sm">
							<Button onClick={handleLoadMore}>Show More Logs</Button>
						</Group>
					)}
				</>
			)}
		</Stack>
	);
}
